import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  LinkIcon,
  ShieldCheckIcon,
  StarIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useMarketplaceAsset, useMarketplaceOrders } from '../../../hooks/useMarketplace';
import useAuthStore from '../../../store/useAuthStore';
import { toast } from 'react-toastify';
import MarketplaceService from '../../../services/marketplaceService';

const MarketplaceAssetDetail = ({ assetId, onClose }) => {
  const { asset, loading } = useMarketplaceAsset(assetId);
  const { createOrder } = useMarketplaceOrders();
  const { isAuthenticated, user } = useAuthStore();
  const navigate = useNavigate();
  
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [buyerMessage, setBuyerMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [assetOrders, setAssetOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  
  // Check if current user is the seller
  const isSeller = asset && user && asset.seller === user.username;
  
  // Fetch orders if user is the seller
  useEffect(() => {
    const fetchOrders = async () => {
      if (isSeller && assetId) {
        setLoadingOrders(true);
        try {
          const orders = await MarketplaceService.getAssetOrders(assetId);
          setAssetOrders(orders);
        } catch (error) {
          console.error('Failed to fetch asset orders:', error);
        } finally {
          setLoadingOrders(false);
        }
      }
    };
    
    fetchOrders();
  }, [isSeller, assetId]);
  
  const getCategoryEmoji = (category) => {
    const emojis = {
      vehicle: '🚗',
      yacht: '⛵',
      property: '🏠',
      luxury: '💎',
      equipment: '🔧',
      other: '📦'
    };
    return emojis[category] || '📦';
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };
  
  const handleBuyNow = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to make a purchase');
      return;
    }
    
    setIsProcessing(true);
    try {
      const orderData = {
        asset_id: asset.asset_id,
        message: buyerMessage.trim()
      };
      
      const order = await createOrder(orderData);
      
      // Success toast is already shown in the hook
      setShowBuyModal(false);
      onClose();
      
      // Navigate to order detail page
      navigate(`/p2p/marketplace/order/${order.order_id}`);
    } catch (error) {
      console.error('Failed to create order:', error);
      // Error toast is already shown in the hook
      // Just log for debugging
    } finally {
      setIsProcessing(false);
    }
  };
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }
  
  if (!asset) {
    return null;
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-[#010615] rounded-xl border border-gray-700 max-w-4xl w-full max-h-[70vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#010615] border-b border-gray-700 p-6 flex items-center justify-between z-10">
          <div className="flex items-center space-x-3">
            <span className="text-4xl">{getCategoryEmoji(asset.category)}</span>
            <div>
              <h2 className="text-2xl font-bold text-white">{asset.title}</h2>
              <p className="text-white text-sm">{asset.category.charAt(0).toUpperCase() + asset.category.slice(1)}</p>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-white" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Price Section */}
          <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-600/30 rounded-xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-white mb-1">Price in BTC1</p>
                <p className="text-4xl font-bold text-white">
                  {parseFloat(asset.price_btc).toFixed(4)}
                  <span className="text-2xl text-purple-400 ml-2">BTC1</span>
                </p>
              </div>
              
              <div>
                <p className="text-sm text-white mb-1">Estimated Value</p>
                <p className="text-3xl font-bold text-white">
                  ${parseFloat(asset.valuation_usd).toLocaleString()}
                  <span className="text-lg text-white ml-2">USD</span>
                </p>
              </div>
            </div>
            
            {isAuthenticated && user?.username !== asset.seller && (
              <button
                onClick={() => setShowBuyModal(true)}
                className="mt-6 w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-all font-bold text-lg shadow-lg"
              >
                🔒 Buy with BTC1 (Escrow Protected)
              </button>
            )}
          </div>
          
          {/* Description */}
          <div>
            <h3 className="text-xl font-bold text-white mb-3">Description</h3>
            <p className="text-white whitespace-pre-wrap">{asset.description}</p>
          </div>
          
          {/* Specifications */}
          {asset.specifications && Object.keys(asset.specifications).length > 0 && (
            <div>
              <h3 className="text-xl font-bold text-white mb-3">Specifications</h3>
              <div className="bg-gray-800 rounded-xl p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(asset.specifications).map(([key, value]) => (
                    <div key={key} className="flex justify-between py-2 border-b border-gray-700">
                      <span className="text-white capitalize">{key.replace(/_/g, ' ')}:</span>
                      <span className="text-white font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Location */}
          <div>
            <h3 className="text-xl font-bold text-white mb-3">Location</h3>
            <div className="flex items-center space-x-2 text-white">
              <MapPinIcon className="h-5 w-5 text-purple-400" />
              <span>{asset.location}</span>
            </div>
          </div>
          
          {/* Contact Information */}
          <div>
            <h3 className="text-xl font-bold text-white mb-3">Contact Seller</h3>
            <div className="bg-gray-800 rounded-xl p-4 space-y-3">
              <div className="whitespace-pre-wrap text-white">
                {asset.contact_info}
              </div>
              
              <button
                onClick={() => copyToClipboard(asset.contact_info)}
                className="mt-2 text-purple-400 hover:text-purple-300 text-sm flex items-center space-x-2"
              >
                <ClipboardDocumentIcon className="h-4 w-4" />
                <span>Copy Contact Info</span>
              </button>
            </div>
          </div>
          
          {/* External Links */}
          {asset.external_links && asset.external_links.length > 0 && (
            <div>
              <h3 className="text-xl font-bold text-white mb-3">Photos & Videos</h3>
              <div className="space-y-2">
                {asset.external_links.map((link, index) => (
                  <a
                    key={index}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    <LinkIcon className="h-4 w-4" />
                    <span className="text-sm">View Media #{index + 1}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
          
          {/* Purchase Requests (Seller Only) */}
          {isSeller && (
            <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-xl p-6 border border-purple-500/30">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                <ShieldCheckIcon className="h-6 w-6 text-purple-400" />
                <span>Purchase Requests</span>
                {assetOrders.length > 0 && (
                  <span className="ml-2 px-3 py-1 bg-purple-500 text-white text-xs rounded-full">
                    {assetOrders.length}
                  </span>
                )}
              </h3>
              
              {loadingOrders ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                </div>
              ) : assetOrders.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-white mb-2">No purchase requests yet</div>
                  <div className="text-sm text-white">You'll see purchase requests here once buyers show interest</div>
                </div>
              ) : (
                <div className="space-y-3">
                  {assetOrders.map((order) => (
                    <div
                      key={order.order_id}
                      className="bg-gray-800 rounded-lg p-4 hover:bg-gray-750 transition-colors cursor-pointer"
                      onClick={() => {
                        onClose();
                        navigate(`/p2p/marketplace/order/${order.order_id}`);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-white" />
                          </div>
                          
                          <div className="flex-1">
                            <div className="font-semibold text-white">
                              {order.buyer_profile?.nickname || 'Buyer'}
                            </div>
                            <div className="text-sm text-white">
                              {order.btc_amount} BTC1 • ${order.usd_value}
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className={`text-sm font-semibold px-3 py-1 rounded-full ${
                            order.status === 'pending' ? 'bg-[#0E1696]/20 text-white' :
                            order.status === 'asset_delivered' ? 'bg-blue-500/20 text-blue-400' :
                            order.status === 'asset_received' ? 'bg-purple-500/20 text-purple-400' :
                            order.status === 'admin_review' ? 'bg-orange-500/20 text-orange-400' :
                            order.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                            'bg-gray-500/20 text-white'
                          }`}>
                            {order.status.replace('_', ' ').toUpperCase()}
                          </div>
                          <div className="text-xs text-white mt-1">
                            {new Date(order.created).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      
                      {order.buyer_message && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="text-xs text-white mb-1">Buyer Message:</div>
                          <div className="text-sm text-white line-clamp-2">{order.buyer_message}</div>
                        </div>
                      )}
                      
                      <div className="mt-3 text-sm text-purple-400 hover:text-purple-300 flex items-center justify-end space-x-1">
                        <EyeIcon className="h-4 w-4" />
                        <span>View Order Details</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Seller Info */}
          <div>
            <h3 className="text-xl font-bold text-white mb-3">Seller Information</h3>
            <div className="bg-gray-800 rounded-xl p-4">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-2xl">
                    {asset.seller_profile?.nickname?.charAt(0).toUpperCase() || 'S'}
                  </span>
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-lg font-bold text-white">
                      {asset.seller_profile?.nickname || asset.seller || 'Seller'}
                    </span>
                    {asset.seller_profile?.is_verified && (
                      <ShieldCheckIcon className="h-5 w-5 text-green-400" />
                    )}
                  </div>
                  
                  {asset.seller_profile && (
                    <div className="flex items-center space-x-4 text-sm text-white">
                      <div className="flex items-center space-x-1">
                        <StarIcon className="h-4 w-4 text-white fill-current" />
                        <span>{asset.seller_profile.rating?.toFixed(1) || '0.0'}</span>
                      </div>
                      <span>{asset.seller_profile.total_trades || 0} trades</span>
                      <span>{asset.seller_profile.completion_rate?.toFixed(0) || 0}% completion</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Buy Confirmation Modal */}
      {showBuyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="bg-[#010615] rounded-xl border border-gray-700 max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-white mb-4">Confirm Purchase</h3>
            
            <div className="space-y-4">
              <div className="bg-gray-800 rounded-lg p-4">
                <p className="text-white text-sm mb-2">You will pay:</p>
                <p className="text-2xl font-bold text-white">
                  {parseFloat(asset.price_btc).toFixed(4)} <span className="text-purple-400">BTC1</span>
                </p>
                <p className="text-white text-sm mt-1">
                  (~${parseFloat(asset.valuation_usd).toLocaleString()} USD)
                </p>
              </div>
              
              <div className="bg-yellow-900/20 border border-[#2A356D]/30 rounded-lg p-4">
                <p className="text-white text-sm">
                  🔒 Your BTC1 will be securely locked in escrow until you confirm receipt of the asset.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Message to Seller (Optional)
                </label>
                <textarea
                  value={buyerMessage}
                  onChange={(e) => setBuyerMessage(e.target.value)}
                  placeholder="Introduce yourself, ask questions, or provide additional details..."
                  rows={4}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowBuyModal(false)}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-3 border border-gray-600 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                
                <button
                  onClick={handleBuyNow}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-3 bg-green-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50"
                >
                  {isProcessing ? 'Processing...' : 'Confirm Purchase'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplaceAssetDetail;

